import React, { Component, CSSProperties, MouseEvent } from 'react';
import { Helmet } from 'react-helmet';
import {
  BundleRoutes,
  BundleRoutesResult,
  BundleRoutesSummary,
  BundleRoutesSummaryResult,
  BundleSource,
} from '../../types/bundleTypes';
import Fetch from 'utils/fetch';
import './Routes.scss';
import { Region } from '../../types/orderTypes';
import { settings } from '../../settings';
import Button, { ButtonGroup } from '../../components/buttons/Button';
import { FaCloudDownloadAlt, FaSyncAlt } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';
import { Loader } from '../../components/Loader/Loader';
import { PlannerMenu } from '../oldPlanner/PlannerMenu';
import { getHubConfig } from '../../const/HubConfig';
import RoutesMap from './RoutesMap';
import RoutesMenu from './RoutesMenu';
import { sum } from '../../utils/array';
import { downloadBlob } from '../../utils/fetchBlob';
import AutoBundlerOptionsModal from '../oldPlanner/planner-map/menu/AutoBundlerOptionsModal';
import {
  AutoBundlerProgressReport,
  runBundlingAlgorithm,
  subscribeToBundler,
} from '../../utils/bundlingService';
import { fetchOrders } from '../../fetch/orderFetch';
import AutoBundlerProgress from '../oldPlanner/AutoBundlerProgress';
import Flatpickr from 'react-flatpickr';

interface State {
  date: Dayjs;
  routeSets: { [key: string]: BundleRoutes };
  routeSetsSummary: BundleRoutesSummary[];
  selectedRouteSet?: BundleRoutes;
  selectedRouteSetId?: string;
  visibleRouteIds: string[];
  isLoading: boolean;
  showMarkerIndex: boolean;
  showAutoBundlerOptionsModal: boolean;
  autoBundlerProgress?: AutoBundlerProgressReport;
  autoBundlerRunning: boolean;
}

interface Props {
  region: Region;
}

export const datePickerInputStyle: CSSProperties = {
  fontSize: '1.5rem',
  paddingTop: '8px',
  paddingBottom: '8px',
  width: '100%',
  maxWidth: '120px',
  textAlign: 'center',
  borderRadius: 'unset',
  borderRight: 'none',
};

export default class Routes extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      date: dayjs(),
      routeSets: {},
      routeSetsSummary: [],
      selectedRouteSet: null,
      selectedRouteSetId: null,
      visibleRouteIds: [],
      isLoading: true,
      showMarkerIndex: false,
      showAutoBundlerOptionsModal: false,
      autoBundlerRunning: false,
    };
  }

  async componentDidMount(): Promise<void> {
    this.refreshState(this.state.date);

    const region = this.props.region;
    subscribeToBundler(
      region,
      'routes',
      () => {
        this.refreshState(this.state.date);
        this.setState({ autoBundlerRunning: false });
      },
      error => {
        this.setState({
          autoBundlerProgress: {
            progress: 0,
            failed: true,
            error: 'Something unexpected occurred when running auto bundle',
            metadata: { region: region },
          },
        });
        if (this.state.autoBundlerRunning) {
          this.setState({ autoBundlerRunning: false });
        }
      },
      progress => {
        this.setState({ autoBundlerRunning: true });
        this.setState({
          autoBundlerProgress: progress,
        });
      }
    );
  }

  async componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: State
  ): Promise<void> {
    if (
      !this.state.date.isSame(prevState.date) ||
      (this.props.region && prevProps.region !== this.props.region)
    ) {
      this.refreshState(this.state.date);
    }
  }

  fetchRoutes = async (id: string): Promise<BundleRoutes[]> => {
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/bundle/routes?id=${id}`,
    });

    if (!response.ok) {
      return [];
    }

    const routesResult: BundleRoutesResult = await response.json();
    return routesResult.routeSets;
  };

  fetchRoutesList = async (
    date: Dayjs,
    region: Region
  ): Promise<BundleRoutesSummary[]> => {
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/bundle/routes-list?date=${date.format(
        'YYYY-MM-DD'
      )}&region=${region}`,
    });

    if (!response.ok) {
      return [];
    }

    const routesResult: BundleRoutesSummaryResult = await response.json();
    return routesResult.routeSets;
  };

  loadRoutes = async (id: string): Promise<void> => {
    this.setState({
      isLoading: true,
    });
    const routeSetsList = await this.fetchRoutes(id);
    const routeSets = routeSetsList && routeSetsList[0];

    this.setState({
      routeSets: {
        ...this.state.routeSets,
        [id]: routeSets,
      },
      isLoading: false,
      selectedRouteSet: routeSets,
      selectedRouteSetId: id,
      visibleRouteIds: routeSets.routes.map(route => route.id),
    });
  };

  refreshState = async (date: Dayjs): Promise<void> => {
    const routeSets = await this.fetchRoutesList(date, this.props.region);

    this.setState({
      routeSets: {},
      routeSetsSummary: routeSets.sort(this.sortRouteSets),
      selectedRouteSet: null,
      visibleRouteIds: [],
      selectedRouteSetId: null,
      isLoading: false,
    });
  };

  sortRouteSets(r1: BundleRoutesSummary, r2: BundleRoutesSummary): number {
    const s1 = r1.source;
    const s2 = r2.source;
    if (s1 !== s2) {
      return s1 === BundleSource.MANUAL ? -1 : 1;
    }
    const l1 = r1.bundlesCount;
    const l2 = r2.bundlesCount;
    return l2 - l1;
  }

  selectRouteSet(id: string) {
    const selectedRouteSet = this.state.routeSets[id];
    if (!selectedRouteSet || selectedRouteSet.routes.length === 0) {
      this.setState({
        selectedRouteSet: null,
        visibleRouteIds: [],
        selectedRouteSetId: null,
      });
      if (id != null) {
        this.loadRoutes(id);
      }
    } else {
      this.setState({
        selectedRouteSet: selectedRouteSet,
        visibleRouteIds: selectedRouteSet.routes.map(route => route.id),
        selectedRouteSetId: id,
      });
    }
  }

  changeDate = async (days: number): Promise<void> => {
    const { date } = this.state;
    const newDate = date.add(days, 'day');
    this.setState({
      date: newDate,
      isLoading: true,
      selectedRouteSetId: null,
    });
  };

  selectDate = async (newDate: Date): Promise<void> => {
    if (!newDate) {
      return;
    }
    this.setState({
      date: dayjs(newDate),
      isLoading: true,
      selectedRouteSetId: null,
    });
  };

  onChangeVisibleRoutes = (routeIds: string[]): void => {
    this.setState({ visibleRouteIds: routeIds });
  };

  routeSetDescription(routes: BundleRoutesSummary): string {
    const source =
      routes.source === BundleSource.MANUAL ? 'Manual bundle' : 'Auto bundle';
    return `${source} | ${routes.ordersCount} orders | ${routes.bundlesCount} bundles`;
  }

  downloadAsJson = (e: MouseEvent) => {
    e.preventDefault();
    const { routeSets, selectedRouteSetId } = this.state;
    const routes = routeSets[selectedRouteSetId];
    if (!routes) {
      return;
    }
    const data = JSON.stringify(routes, null, 2);
    const blob = new Blob([data], { type: 'application/json' });

    const ordersBundleCount = routes.routes.map(
      route => new Set(route.steps.map(s => s.orderId)).size
    );
    const orderCount = sum(ordersBundleCount);
    const source = routes.source === BundleSource.MANUAL ? 'manual' : 'auto';
    downloadBlob(
      blob,
      `routes-${routes.date}-${source}-${orderCount}_orders-${routes.routes.length}_bundles.json`,
      true
    );
  };

  autoBundleOrders() {
    this.setState({
      showAutoBundlerOptionsModal: true,
    });
  }

  async runAutoBundling(timePerStop: number, hubDeparture: string) {
    const selectedRouteSet = this.state.selectedRouteSet;
    const orderIds = selectedRouteSet.routes.flatMap(route =>
      route.steps.map(step => step.orderId)
    );
    const orders = await fetchOrders(orderIds);
    const { region } = this.props;
    const date = dayjs(selectedRouteSet.date);

    const bundlingStarted = await runBundlingAlgorithm(
      orders,
      date,
      region,
      timePerStop,
      hubDeparture,
      false,
      'routes',
      () => {
        this.setState({ autoBundlerRunning: false });
      },
      error => {
        this.setState({
          autoBundlerProgress: {
            progress: 0,
            failed: true,
            error: 'Something unexpected occurred when running auto bundle',
            metadata: { region: region },
          },
        });
        if (this.state.autoBundlerRunning) {
          this.setState({ autoBundlerRunning: false });
        }
      },
      progress => {
        this.setState({
          autoBundlerProgress: progress,
        });
      }
    );
    if (bundlingStarted) {
      this.setState({ autoBundlerRunning: true });
      this.setState({
        autoBundlerProgress: {
          progress: 0,
          failed: false,
          metadata: {
            region: region,
          },
        },
      });
    }
  }

  render() {
    const { region } = this.props;
    const {
      date,
      selectedRouteSet,
      visibleRouteIds,
      showMarkerIndex,
      routeSetsSummary,
      selectedRouteSetId,
      showAutoBundlerOptionsModal,
      autoBundlerProgress,
    } = this.state;
    const visibleRoutes = (
      (selectedRouteSet && selectedRouteSet.routes) ||
      []
    ).filter(route => visibleRouteIds.includes(route.id));
    const bundlerFailed = autoBundlerProgress?.failed;
    console.log('visibleRoutes', visibleRoutes);
    return (
      <>
        <Helmet>
          <title>Planner Routes</title>
        </Helmet>
        <PlannerMenu />
        <div className="routes">
          {this.state.isLoading && (
            <div className="routes-loader">
              <div className="routes-loader-title">
                <Loader type="spinner" size="large" />
              </div>
            </div>
          )}
          <div className="routes__filter-menu">
            <div className="filter-menu__header">
              {autoBundlerProgress && !bundlerFailed && (
                <AutoBundlerProgress
                  autoBundlerProgress={autoBundlerProgress}
                />
              )}
              <div className="filter-menu__header-date">
                <div className="filter-menu__header-date-refresh">
                  <Button
                    title={<FaSyncAlt />}
                    onClick={() => this.refreshState(date)}
                  />
                </div>
                <ButtonGroup>
                  <Button title="<" onClick={() => this.changeDate(-1)} />
                  <Flatpickr
                    onClose={selectedDates =>
                      selectedDates && this.selectDate(selectedDates[0])
                    }
                    value={date.toDate()}
                    options={{
                      dateFormat: 'D d. M',
                      shorthandCurrentMonth: true,
                      locale: { firstDayOfWeek: 1 },
                    }}
                    render={(props, ref) => (
                      <input
                        style={datePickerInputStyle}
                        className={'form-control'}
                        type="text"
                        ref={ref}
                      />
                    )}
                  />
                  <Button title=">" onClick={() => this.changeDate(1)} />
                </ButtonGroup>
              </div>
              <div className="routes__filter-label__form-group">
                <label>
                  <span className="routes__filter-label">Bundle sets:</span>
                  <select
                    onChange={e => this.selectRouteSet(e.target.value)}
                    value={
                      selectedRouteSetId != null ? selectedRouteSetId : '-'
                    }
                  >
                    <option value="-" disabled={true}>
                      {routeSetsSummary.length > 0
                        ? `${routeSetsSummary.length} bundling results`
                        : 'No bundles found'}
                    </option>
                    {routeSetsSummary.map((routeSet, idx) => (
                      <option key={`route-select-${idx}`} value={routeSet.id}>
                        {this.routeSetDescription(routeSet)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div
                style={{
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div className="routes__filter-status">
                  {selectedRouteSetId != null && (
                    <span
                      onClick={e => this.downloadAsJson(e)}
                      className="routes__filter-download"
                    >
                      <FaCloudDownloadAlt /> <span>Download JSON</span>
                    </span>
                  )}
                </div>
                {selectedRouteSetId != null && (
                  <div className="routes__filter-status">
                    <Button
                      title="Auto bundle"
                      type="slim"
                      onClick={() => this.autoBundleOrders()}
                    />
                  </div>
                )}
                <div className="routes__filter-status">
                  <label htmlFor="routes-show-position-check">
                    <input
                      type="checkbox"
                      id="routes-show-position-check"
                      checked={showMarkerIndex}
                      onChange={event => {
                        this.setState({ showMarkerIndex: !showMarkerIndex });
                      }}
                    />{' '}
                    <span>Show steps index</span>
                  </label>
                </div>
              </div>
            </div>
            <RoutesMenu
              date={date}
              region={region}
              bundleRoutes={selectedRouteSet}
              metrics={selectedRouteSet?.metrics}
              onChangeVisibleRoutes={this.onChangeVisibleRoutes}
            />
          </div>

          <RoutesMap
            routes={visibleRoutes}
            hubPositions={(() => {
              const location = getHubConfig(region)?.hubAddress.location;
              return !!location ? [location] : [];
            })()}
            showMarkerIndex={showMarkerIndex}
          />
          {showAutoBundlerOptionsModal && (
            <AutoBundlerOptionsModal
              region={region}
              skipCreate={true}
              onCloseModal={() =>
                this.setState({
                  showAutoBundlerOptionsModal: false,
                })
              }
              onOptionsSelected={async (timePerStop, hubDeparture) => {
                this.setState({ showAutoBundlerOptionsModal: false });
                this.runAutoBundling(timePerStop, hubDeparture);
              }}
            />
          )}
        </div>
      </>
    );
  }
}
